import { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import './Onboarding.css';

const LOCATIONS = ['Europe', 'North America', 'South America', 'Asia', 'Africa', 'Oceania'];
const SECURITY_INTEREST = ['Very', 'Somewhat', 'Barely', 'Not at all'];
const BTN_LABELS = ['Continue', 'Continue', 'Save'];
const TOTAL_STEPS = 3;
const AGES = Array.from({ length: 83 }, (_, i) => i + 18);

export default function Profile() {
  const history = useHistory();

  const [step, setStep]               = useState(0);
  const [location, setLocation]       = useState(null);
  const [age, setAge]                 = useState(30);
  const [securityInterest, setSecInt] = useState(null);
  const [animKey, setAnimKey]         = useState(0);

  const ageScrollRef  = useRef(null);
  const ageTimeoutRef = useRef(null);

  // ── Scroll age picker to default when step 1 opens ──
  useEffect(() => {
    if (step !== 1) return;
    setTimeout(() => scrollAgeTo(age, false), 80);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollAgeTo = (targetAge, smooth = true) => {
    const el = ageScrollRef.current;
    if (!el) return;
    const item = el.querySelector(`[data-age="${targetAge}"]`);
    if (!item) return;
    const top = item.offsetTop - el.clientHeight / 2 + item.clientHeight / 2;
    el.scrollTo({ top, behavior: smooth ? 'smooth' : 'instant' });
  };

  const onAgeScroll = useCallback(() => {
    clearTimeout(ageTimeoutRef.current);
    ageTimeoutRef.current = setTimeout(() => {
      const el = ageScrollRef.current;
      if (!el) return;
      const center = el.scrollTop + el.clientHeight / 2;
      let best = 18, bestDist = Infinity;
      el.querySelectorAll('[data-age]').forEach(item => {
        const mid = item.offsetTop + item.clientHeight / 2;
        const dist = Math.abs(center - mid);
        if (dist < bestDist) { bestDist = dist; best = +item.dataset.age; }
      });
      setAge(best);
    }, 80);
  }, []);

  const isNextEnabled = (() => {
    if (step === 0) return location !== null;
    if (step === 1) return true; // age always selected
    return securityInterest !== null;
  })();

  const goTo = (idx) => {
    setAnimKey(k => k + 1);
    setStep(idx);
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS - 1) goTo(step + 1);
    else history.push('/takes');
  };

  const handleBack = () => {
    if (step > 0) goTo(step - 1);
    else history.goBack();
  };

  return (
    <div className="ob-root">

      {/* ── Progress bar ── */}
      <div className="ob-progress">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div
            key={i}
            className={`ob-pb${i < step ? ' done' : i === step ? ' active' : ''}`}
          />
        ))}
        <span className="ob-pb-label">{step + 1} / {TOTAL_STEPS}</span>
      </div>

      {/* ── Step content ── */}
      <div className="ob-body" key={animKey}>

        {/* Step 0: Location */}
        {step === 0 && (
          <div className="ob-step">
            <div className="ob-icon">
              <svg viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
            </div>
            <h1 className="ob-title">Where are you from?</h1>
            <p className="ob-desc">This helps us compare perspectives from different regions around the world.</p>

            <div className="ob-options">
              {LOCATIONS.map(loc => (
                <button
                  key={loc}
                  className={`ob-option${location === loc ? ' selected' : ''}`}
                  onClick={() => setLocation(loc)}
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Age */}
        {step === 1 && (
          <div className="ob-step">
            <div className="ob-icon">
              <svg viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
            </div>
            <h1 className="ob-title">How old are you?</h1>
            <p className="ob-desc">Your age group helps us present meaningful demographic insights alongside voting results.</p>

            <div className="ob-age-picker">
              <div className="ob-age-fade ob-age-fade--top" />
              <div className="ob-age-fade ob-age-fade--bottom" />
              <div className="ob-age-highlight" />
              <div className="ob-age-scroll" ref={ageScrollRef} onScroll={onAgeScroll}>
                <div className="ob-age-spacer" />
                {AGES.map(a => (
                  <div
                    key={a}
                    data-age={a}
                    className={`ob-age-item${age === a ? ' selected' : ''}`}
                    onClick={() => { setAge(a); scrollAgeTo(a); }}
                  >
                    {a}
                  </div>
                ))}
                <div className="ob-age-spacer" />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Interest in Security Policy */}
        {step === 2 && (
          <div className="ob-step">
            <div className="ob-icon">
              <svg viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
            </div>
            <h1 className="ob-title">How interested are you in security policy?</h1>
            <p className="ob-desc">This helps us show how voting results differ between people with varying levels of interest in security and defence topics.</p>

            <div className="ob-options">
              {SECURITY_INTEREST.map(opt => (
                <button
                  key={opt}
                  className={`ob-option${securityInterest === opt ? ' selected' : ''}`}
                  onClick={() => setSecInt(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* ── Fixed bottom buttons ── */}
      <div className="ob-btns">
        <button className="ob-btn-back" onClick={handleBack}>Back</button>
        <button
          className="ob-btn-next"
          onClick={handleNext}
          disabled={!isNextEnabled}
        >
          {BTN_LABELS[step]}
        </button>
      </div>

    </div>
  );
}
